// verdictClass.js — the single honest semantic for an oracle verdict (no-silent-green / M1).
// @arch:pure-engine
//
// AUDIT FINDING (internal + 2024–2026 literature, convergent): the LAW's sharpest exposure is
// "silent green" — when a code-changing turn injects NO executable verifier, the path coerces
// noVerifiers → green ("nobody judged, therefore DONE"). It is honest (it never claims an unrun
// green) but INVISIBLE, and three consumers disagreed on the meaning (taskOracle/verifyTask treat
// noVerifiers as green; checkpoint.isHonestGreen correctly treats it as NOT green). The literature
// (One Token to Fool LLM-as-a-Judge; ImpossibleBench; Sycophancy→Subterfuge) says the
// invariant-preserving fix is to make the absence of an executable verifier an EXPLICIT state, not
// an implicit pass. This module is that single source of truth.
//
// PURE engine (Tier 1): no I/O. THE LAW is unchanged — 'unverified' is NOT promoted, NOT recorded
// as a verified green, and the no-confidence-gate invariant is untouched (this introduces no score
// or model self-judgment; it only labels the ALREADY-honest no-op honestly).

/**
 * Classify an oracle verdict into one honest class.
 * @param {object} v - typically runOracle's output { reachedGreen, noVerifiers }.
 * @returns {('green'|'unverified'|'red')}
 *   'green'      a real executable verifier ran and passed.
 *   'unverified' NO executable verifier matched — nobody judged (explicit, not a verified green).
 *   'red'        a verifier ran and failed.
 */
export function classifyVerdict({ reachedGreen = false, noVerifiers = false } = {}) {
  if (reachedGreen) return 'green';
  if (noVerifiers) return 'unverified';
  return 'red';
}

/** True only for a real verified green — never for the unverified no-op. */
export const isVerifiedGreen = (v) => classifyVerdict(v) === 'green';

/**
 * Does the DAG executor consider this task COMPLETE (not blocking)? An unverified no-op still
 * completes the task (it never false-blocks — that was the original intent of noVerifiers→green);
 * the difference is purely that it is LABELED 'unverified', so reporting/learning can tell a
 * verified green from an unverified one. Byte-compatible with today's `reachedGreen || noVerifiers`.
 */
export const completesTask = (v) => classifyVerdict(v) !== 'red';

export default { classifyVerdict, isVerifiedGreen, completesTask };
